import { useEffect, useState } from "react";
import { Autocomplete, useLoadScript } from "@react-google-maps/api";
import { extractLocationDetails } from "../../utils/stringUtils";
import { InputText } from "primereact/inputtext";

interface SearchAddressProps {
  id: string;
  name: string;
  error?: string;
  label?: string;
  value?: string;
  disabled?: boolean;
  onChange: (event: {
    target: {
      value: string;
      latitude?: number;
      longitude?: number;
      place?: any;
      locationDetails?: {
        addressLine1?: string;
        addressLine2?: string;
        city?: string;
        state?: string;
        country?: string;
        postalCode?: string;
      };
    };
  }) => void;
  required?: boolean;
  placeholder?: string;
  labelClass?: string;
  width?: string;
  isSearch?: boolean;
  className?: any;
}
const SearchAddress: React.FC<SearchAddressProps> = ({
  id,
  name,
  value,
  onChange,
  placeholder,
  disabled,
  className,
  isSearch = false,
}) => {
  const [inputValue, setInputValue] = useState(value ?? "");
  const [searchResult, setSearchResult] = useState<google.maps.places.Autocomplete | null>(null);
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
    libraries: ["places"],
  });

  useEffect(() => {
    setInputValue(value ?? "");
  }, [value]);

  const autocompleteOptions: google.maps.places.AutocompleteOptions = {
    componentRestrictions: { country: "us" },
    fields: ["address_components", "formatted_address", "geometry", "name", "place_id"],
    types: isSearch ? ["geocode"] : ["address"],
  };

  const emitManualChange = (nextValue: string) => {
    onChange({ target: { value: nextValue } });
  };

  const buildDisplayAddress = (place: google.maps.places.PlaceResult, locationDetails: ReturnType<typeof extractLocationDetails>) => {
    if (place.formatted_address) {
      return place.formatted_address;
    }

    return [
      locationDetails.addressLine1,
      locationDetails.city,
      locationDetails.state,
      locationDetails.postalCode,
    ]
      .filter(Boolean)
      .join(", ");
  };

  function onLoad(autocomplete: google.maps.places.Autocomplete) {
    setSearchResult(autocomplete);
  }

  function onPlaceChanged() {
    if (!searchResult) {
      return;
    }

    const place = searchResult.getPlace();
    const hasAddressData = place.address_components?.length || place.formatted_address || place.name;

    if (!hasAddressData) {
      emitManualChange(inputValue.trim());
      return;
    }

    const locationDetails = extractLocationDetails(place);
    const address = buildDisplayAddress(place, locationDetails);
    const latitude = place.geometry?.location?.lat();
    const longitude = place.geometry?.location?.lng();

    setInputValue(address);
    onChange({ target: { value: address, latitude, longitude, place, locationDetails } });
  }

  if (!isLoaded) {
    return (
      <div className="app-address-picker app-address-picker-loading">
        <InputText
          id={id}
          name={name}
          className={`w-100 custom-input app-address-picker-input ${className ?? ""}`.trim()}
          value={inputValue}
          placeholder="Loading address search..."
          disabled
        />
      </div>
    );
  }

  return (
    <div className="app-address-picker-shell">
      <Autocomplete onLoad={onLoad} onPlaceChanged={onPlaceChanged} options={autocompleteOptions}>
        <div className={`app-address-picker ${disabled ? "app-address-picker-disabled" : ""}`.trim()}>
          <InputText
            id={id}
            name={name}
            className={`w-100 custom-input app-address-picker-input ${className ?? ""}`.trim()}
            value={inputValue}
            placeholder={placeholder ?? "Search address ..."}
            title={placeholder ?? "Search address ..."}
            disabled={disabled}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck={false}
            data-lpignore="true"
            data-form-type="other"
            onChange={(e) => {
              const nextValue = e.target.value;
              setInputValue(nextValue);
              emitManualChange(nextValue);
            }}
            onBlur={() => {
              const trimmedValue = inputValue.trim();
              if (trimmedValue !== inputValue) {
                setInputValue(trimmedValue);
              }
              if (!trimmedValue) {
                emitManualChange("");
              }
            }}
          />
        </div>
      </Autocomplete>
    </div>
  );
};

export default SearchAddress;
